// A private region is a screen whose text must never reach a session replay:
// every page under /courses, the highlight drawer, and the Notes Hub dialogs
// and leader sheets that teleport out of the page root. Two vendors record replays and each has
// its own opt-out attribute, so this applies both from one place and a spec
// (tests/unit/courses/private-regions.spec.js) holds every /courses route to
// it. docs/plans/2026-09-02-next-steps-e2e-encryption.md, section 7.
//
// Mixpanel masks text nodes under `[data-private]` (src/utils/mixpanel.js);
// Datadog masks under `data-dd-privacy="mask"`. Neither stops the recording:
// layout, taps and scrolling are still captured, the words are asterisked.
import { PRIVATE_REGION_ATTRS } from '@/constants/replay-privacy';

/** Stamp both attributes on an element; a no-op for anything that is not one. */
function markPrivateRegion(el) {
  if (!el || el.nodeType !== 1) return;
  Object.entries(PRIVATE_REGION_ATTRS).forEach(([name, value]) => {
    if (el.getAttribute(name) !== value) el.setAttribute(name, value);
  });
}

// The element(s) a component renders at its root. A template that opens with a
// comment (most Next Steps pages do) compiles to a fragment in development, so
// `$el` is the fragment's empty text anchor and the real root is a sibling
// before the fragment's end anchor. A <Teleport> root has no anchor and no
// element here; that case binds `privateRegionAttrs` in its template instead.
function rootElements(vm) {
  const el = vm.$el;
  if (!el) return [];
  if (el.nodeType === 1) return [el];
  // `$` is Vue's internal instance, not public API; `subTree.anchor` is the
  // fragment's end marker. A production build strips template comments, so a
  // comment-led template is a plain element root there and this walk is only
  // taken in development and in tests (private-region.spec.js pins it).
  const end = vm.$?.subTree?.anchor;
  if (!end) return [];
  const found = [];
  for (let node = el.nextSibling; node && node !== end; node = node.nextSibling) {
    if (node.nodeType === 1) found.push(node);
  }
  return found;
}

export default {
  computed: {
    // For a template whose root is a <Teleport> (so `$el` is a placeholder, not
    // an element): `v-bind="privateRegionAttrs"` on the element inside it.
    privateRegionAttrs() {
      return PRIVATE_REGION_ATTRS;
    },
  },
  mounted() {
    rootElements(this).forEach(markPrivateRegion);
  },
  // A root that is swapped by a v-if/v-else re-render is a new element, so the
  // stamp is re-applied after every update. Cheap: two attribute reads.
  updated() {
    rootElements(this).forEach(markPrivateRegion);
  },
};
